import { observer, useLocalObservable } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useLoaderData, useParams } from 'react-router';

import { AuditCreation, TemplateManager } from '@ifs-gcp/core';

import type { DashboardLoaderData } from '@/app/routes/dashboard/loader';
import { useAvailableActions } from '@/hooks/useAvailableActions';
import { useUserStore } from '@/store/userStore';

import { DashboardElement } from './dashboard-element';
import { DashboardModal } from './dashboard-modal';
import styles from './dashboard.module.scss';

export const Dashboard = observer(() => {
  const { standards, dashboardTemplate, startProcessTemplate, modulesInUse, availableActions } =
    useLoaderData<DashboardLoaderData>();
  const user = useUserStore((state) => state.userProvider);
  const setModuleIdsInUse = useUserStore((state) => state.setModuleIdsInUse);
  const { setAvailableActions } = useAvailableActions();

  const { supplier_id: supplierId = '' } = useParams();

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setAvailableActions('global', availableActions?.actions || []);
    setAvailableActions('auditCase', []);
  }, [availableActions]);

  useEffect(() => {
    if (Array.isArray(modulesInUse?.moduleIds)) {
      setModuleIdsInUse(modulesInUse.moduleIds);
    }
  }, [modulesInUse, setModuleIdsInUse]);

  const domain = useLocalObservable(() => new AuditCreation(standards, user));

  const handleModalClose = () => {
    Array.from(domain.modulesHandler.modules.keys()).forEach((moduleId) =>
      domain.modulesHandler.deleteModule(moduleId),
    );
    setIsModalOpen(false);
  };

  const dashboardTemplateHandler = useLocalObservable(() => {
    return new TemplateManager(
      dashboardTemplate.templateCode,
      domain.conditionHandler,
      domain.modulesHandler,
      user,
    );
  });

  const startProcessTemplateHandler = useLocalObservable(() => {
    return new TemplateManager(
      startProcessTemplate.templateCode,
      domain.conditionHandler,
      domain.modulesHandler,
      user,
    );
  });

  if (!supplierId) {
    throw new Error('Supplier id is missing');
  }

  return (
    <div className={styles.page}>
      <DashboardElement
        domain={domain}
        element={dashboardTemplateHandler.schema}
        supplierId={supplierId}
        handleOpenModal={() => setIsModalOpen(true)}
      />

      {isModalOpen && (
        <DashboardModal
          domain={domain}
          supplierId={supplierId}
          standards={standards}
          userProvider={user}
          handleCloseModal={handleModalClose}
          startProcessTemplateHandler={startProcessTemplateHandler}
        />
      )}
    </div>
  );
});
